import {
  TContractDocument,
  TContractDocumentType,
  TCreateOrganizationContractBE,
  TOrganizationContract,
  TOrganizationContractServiceType,
} from '@/types/Organization.types';
import { api, TCustomAxiosPaginationResponse } from '..';
import { MIN_CHARS_TO_DO_SEARCH } from '@/constants/numberVars';
import { TContractFilterOption, TContractStatus } from '@/types/Contracts.types';
import { label4BEMapper } from '@/utils/mappers/label';
import { contractStatus4BEMapper } from '@/utils/mappers/contractStatus';
import { TContractAllApiResponse } from '@/types/api/Contract.types';
import { apiPaths } from '@/services/api/paths';
import { SUPER_ADMIN_PAGE_SIZE } from '@/constants/numberVars';

export const getContracts = async (
  page: number,
  filterOption: TContractFilterOption,
  filterText: string,
  sortingColumn?: string,
  sortingDirection?: string,
): Promise<TCustomAxiosPaginationResponse<TContractAllApiResponse[]>> => {
  const params: { [key: string]: string | number } = {
    limit: SUPER_ADMIN_PAGE_SIZE,
    page,
  };

  if (filterText && filterText?.length >= MIN_CHARS_TO_DO_SEARCH) {
    const filter = label4BEMapper(filterOption);

    const isStatusFilter = filter === 'status';
    if (isStatusFilter) {
      params[filter] = contractStatus4BEMapper(filterText as TContractStatus);
    }

    if (!isStatusFilter) {
      params[filter] = filterText;
    }
  }

  const sortBy = label4BEMapper(sortingColumn);

  params['sortBy'] = sortBy;
  params['sortOrder'] = sortingDirection || 'asc';

  const { data } = await api.get<TCustomAxiosPaginationResponse<TContractAllApiResponse[]>>(apiPaths.contracts.root, {
    params,
  });

  return data;
};

export const getOrganizationContracts = async (
  orgId: string,
  page: number,
): Promise<TCustomAxiosPaginationResponse<TOrganizationContract[]>> => {
  const params: { limit: number; page: number } = {
    limit: SUPER_ADMIN_PAGE_SIZE,
    page,
  };

  const { data } = await api.get<TCustomAxiosPaginationResponse<TOrganizationContract[]>>(
    apiPaths.contracts.organizationContracts(orgId),
    {
      params,
    },
  );

  return data;
};

export const getOrganizationActiveContract = async (orgId: string): Promise<TOrganizationContract> => {
  const { data } = await api.get<{ data: TOrganizationContract }>(
    apiPaths.contracts.organizationActiveContract(orgId),
  );

  return data.data;
};

export const createOrganizationContract = async (
  orgId: string,
  newContract: TCreateOrganizationContractBE,
): Promise<TCustomAxiosPaginationResponse<TOrganizationContract>> => {
  const { data } = await api.post<TCustomAxiosPaginationResponse<TOrganizationContract>>(
    apiPaths.contracts.organizationContracts(orgId),
    newContract,
  );

  return data;
};

export const editContractTotal = async (
  contractId: string,
  editedContractServiceType: TOrganizationContractServiceType,
  changeAmount: number,
): Promise<TCustomAxiosPaginationResponse<TOrganizationContract>> => {
  const { data } = await api.patch<TCustomAxiosPaginationResponse<TOrganizationContract>>(
    apiPaths.contracts.total(contractId),
    {
      services: {
        [editedContractServiceType]: changeAmount,
      },
    },
  );

  return data;
};

export const editContractUsed = async (
  contractId: string,
  editedContractServiceType: TOrganizationContractServiceType,
  changeAmount: number,
): Promise<TCustomAxiosPaginationResponse<TOrganizationContract>> => {
  const { data } = await api.patch<TCustomAxiosPaginationResponse<TOrganizationContract>>(
    apiPaths.contracts.used(contractId),
    {
      services: {
        [editedContractServiceType]: changeAmount,
      },
    },
  );

  return data;
};

export const updateContract = async (
  contract: TOrganizationContract,
): Promise<TCustomAxiosPaginationResponse<TOrganizationContract>> => {
  const { id, organizationId, history, createdBy, createdAt, updatedBy, updatedAt, ...contractWithoutIds } = contract;
  const { data } = await api.patch<TCustomAxiosPaginationResponse<TOrganizationContract>>(
    apiPaths.contracts.byId(contract.id),
    contractWithoutIds,
  );

  return data;
};

export const getContractDocuments = async (contractId: string): Promise<TContractDocument[]> => {
  const { data } = await api.get<{ data: TContractDocument[] }>(apiPaths.contracts.documents(contractId));

  return data.data;
};

export const uploadContractDocument = async (
  contractId: string,
  documentType: TContractDocumentType,
  newContractDocument: File,
  description?: string,
): Promise<{ success: true }> => {
  const formData = new FormData();
  formData.append('type', documentType);

  formData.append('file', new File([newContractDocument], newContractDocument.name, { type: 'application/pdf' }));

  if (description) {
    formData.append('description', description);
  }

  const { data } = await api.post(apiPaths.contracts.documents(contractId), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const resultData = data.data;

  return resultData;
};

export const deleteContractDocument = async (contractId: string, documentKey: string): Promise<{ success: true }> => {
  const { data } = await api.delete(apiPaths.contracts.documents(contractId), { data: { key: documentKey } });

  const resultData = data.data;

  return resultData;
};
